import {
  generateSectionWithComponent,
  componentsConfig,
  DATA_SOURCE_KEY_MAPPING,
  COMPONENT_TYPE_MAPPING,
  DATA_SOURCE_TO_COMPONENT_TYPE_MAPPING,
  getUniqueFacebookPhotos,
} from '@bingads-webui-campaign-react/smart-component-util';
import Promise from 'bluebird';
import _ from 'underscore';

export const addDefaultComponentsForFBScenario = (fbDataSource, pilots = {}) => {
  const {
    isSmartPageImageTextSuggestionEnabled,
    isSmartPageBusinessHourSectionEnabled,
  } = pilots;
  let newComponents = [];
  const dataSource = { ...fbDataSource };

  const dataSourceForAddDefaultComponent =
    [
      DATA_SOURCE_KEY_MAPPING.Photos,
      DATA_SOURCE_KEY_MAPPING.Posts,
      DATA_SOURCE_KEY_MAPPING.Reviews,
    ];

  if (isSmartPageImageTextSuggestionEnabled) {
    // In this pilot, FB desc is moved to BusinessSummary
    dataSourceForAddDefaultComponent.push(DATA_SOURCE_KEY_MAPPING.BusinessSummary);
  } else {
    // Originally we create about us section from FB description
    dataSourceForAddDefaultComponent.push(DATA_SOURCE_KEY_MAPPING.BusinessDescription);
  }

  if (isSmartPageBusinessHourSectionEnabled) {
    dataSourceForAddDefaultComponent.push(DATA_SOURCE_KEY_MAPPING.BusinessHours);
  }

  _.each(dataSourceForAddDefaultComponent, (dataType) => {
    if (!_.isEmpty(dataSource[dataType])) {
      const componentType = DATA_SOURCE_TO_COMPONENT_TYPE_MAPPING[dataType];
      const newSection =
        generateSectionWithComponent({
          componentType,
          sectionIndex: componentsConfig[componentType].order,
        });
      newComponents = newComponents.concat(newSection);
    }
  });

  return newComponents;
};

export const addConfigForComponents = (components, dataSource, pilots = {}) => {
  const {
    isSmartPageImageTextSuggestionEnabled,
    isSmartPageBusinessHourSectionEnabled,
  } = pilots;
  const newComponents = [...components];

  _.each(newComponents, (component) => {
    if (component.type === COMPONENT_TYPE_MAPPING.CoverImage) {
      const coverImage = dataSource[DATA_SOURCE_KEY_MAPPING.CoverImage] || {};
      _.extend(component, {
        imageId: coverImage.Id,
        imageWidth: coverImage.Width,
        imageHeight: coverImage.Height,
        // Currently ExternalSrc could only set by image suggestion
        externalSrc: coverImage.ExternalSrc,
      });
    }
    if (component.type === COMPONENT_TYPE_MAPPING.Gallery) {
      _.extend(component, { photos: dataSource[DATA_SOURCE_KEY_MAPPING.Photos] });
    }
    if (component.type === COMPONENT_TYPE_MAPPING.Update) {
      _.extend(component, { updates: dataSource[DATA_SOURCE_KEY_MAPPING.Posts] });
    }
    if (component.type === COMPONENT_TYPE_MAPPING.Review) {
      _.extend(component, { reviews: dataSource[DATA_SOURCE_KEY_MAPPING.Reviews] });
    }
    if (component.type === COMPONENT_TYPE_MAPPING.Summary) {
      _.extend(component, {
        businessSummary: isSmartPageImageTextSuggestionEnabled ?
          dataSource[DATA_SOURCE_KEY_MAPPING.BusinessSummary]
          : dataSource[DATA_SOURCE_KEY_MAPPING.BusinessDescription],
      });
    }
    if (
      component.type === COMPONENT_TYPE_MAPPING.BusinessHours
      && isSmartPageBusinessHourSectionEnabled
    ) {
      _.extend(component, { businessHours: dataSource[DATA_SOURCE_KEY_MAPPING.BusinessHours] });
    }
  });

  return newComponents;
};

const getImageUrls = (imageService, imageIds) => imageService.batchGet(imageIds);

const transformPosts = (originUpdates, imagesInfo) => {
  let imageIndex = 0;
  const updates = [];

  _.map(originUpdates, (originalUpdate) => {
    const update = {};
    update.id = originalUpdate.Id;
    update.message = originalUpdate.Message;
    update.createdTime = originalUpdate.CreatedTime;

    if (imageIndex < imagesInfo.length && originalUpdate.FullPicture) {
      update.imageId = imagesInfo[imageIndex].IdString;
      update.photo = imagesInfo[imageIndex].ImageUrl;
      imageIndex += 1;
    }

    updates.push(update);
  });

  return updates;
};

const transformReviews = (originReviews, imagesInfo, businessName) => {
  let imageIndex = 0;
  const reviews = [];

  _.map(originReviews, (originReview) => {
    const review = {};
    review.reviewText = originReview.ReviewText;
    review.recommendationType = originReview.RecommendationType;
    review.createdTime = originReview.CreatedTime;
    review.businessName = businessName;

    if (originReview.Reviewer) {
      review.id = `${originReview.CreatedTime}-${originReview.Reviewer.Id}`;
      review.reviewer = {
        name: originReview.Reviewer.Name,
        imageId: '',
        picture: '',
      };

      if (imageIndex < imagesInfo.length
        && originReview.Reviewer.Picture
        && originReview.Reviewer.Picture.Url
        && imagesInfo[imageIndex].IdString
      ) {
        review.reviewer.imageId = imagesInfo[imageIndex].IdString;
        review.reviewer.picture = imagesInfo[imageIndex].ImageUrl;
        imageIndex += 1;
      }
    }

    reviews.push(review);
  });

  return reviews;
};

export const batchTransformImagesInComponent =
  (imageService, images, handleErrors) =>
    imageService.bulkUpsert(images, handleErrors)
      .then((results) => {
        const imageIds = _.compact(results.map(res => res.imageId)); // filter out errored ones
        return getImageUrls(imageService, imageIds);
      });

export const batchTransformImagesInFBInfo = (dataSource, imageService, handleErrors) => {
  const MAX_GALLERY_NUM = 4;
  const MAX_UPDATES_NUM = 3;
  const MAX_REVIEWS_NUM = 3;
  const newDataSource = { ...dataSource };

  const originCoverImagePromise = dataSource[DATA_SOURCE_KEY_MAPPING.CoverImage] ?
    batchTransformImagesInComponent(
      imageService,
      [dataSource[DATA_SOURCE_KEY_MAPPING.CoverImage]],
      handleErrors
    ) : [];

  // TODO need to confirm with Tang about this attr name
  const originLogoImagePromise = dataSource.profilePicture ?
    batchTransformImagesInComponent(imageService, [dataSource.profilePicture], handleErrors) : [];

  const originalImages = getUniqueFacebookPhotos(dataSource[DATA_SOURCE_KEY_MAPPING.Photos]);
  let originGalleryImagesUrls = [];
  _.each(originalImages, (item) => {
    originGalleryImagesUrls.push(item.Images[0].Url);
  });

  if (originGalleryImagesUrls.length > MAX_GALLERY_NUM) {
    originGalleryImagesUrls = originGalleryImagesUrls.slice(0, MAX_GALLERY_NUM);
  }

  const originGalleryImagesUrlsPromise = _.isEmpty(originGalleryImagesUrls) ? [] :
    batchTransformImagesInComponent(imageService, originGalleryImagesUrls, handleErrors);

  let originUpdates = dataSource[DATA_SOURCE_KEY_MAPPING.Posts];
  if (originUpdates.length > MAX_UPDATES_NUM) {
    originUpdates = originUpdates.slice(0, MAX_UPDATES_NUM);
  }

  let originUpdatesImagesUrls = [];
  _.each(originUpdates, (item) => {
    originUpdatesImagesUrls.push(item.FullPicture);
  });
  originUpdatesImagesUrls = _.compact(originUpdatesImagesUrls);

  const originUpdatesImagesUrlsPromise = _.isEmpty(originUpdatesImagesUrls) ? [] :
    batchTransformImagesInComponent(imageService, originUpdatesImagesUrls, handleErrors);


  let originReviews = dataSource[DATA_SOURCE_KEY_MAPPING.Reviews];
  if (originReviews.length > MAX_REVIEWS_NUM) {
    originReviews = originReviews.slice(0, MAX_REVIEWS_NUM);
  }

  let originReviewsImagesUrls = [];
  _.each(originReviews, (item) => {
    if (item.Reviewer && item.Reviewer.Picture) {
      originReviewsImagesUrls.push(item.Reviewer.Picture.Url);
    }
  });
  originReviewsImagesUrls = _.compact(originReviewsImagesUrls);

  const originReviewsImagesUrlsPromise = _.isEmpty(originReviewsImagesUrls) ? [] :
    batchTransformImagesInComponent(imageService, originReviewsImagesUrls, handleErrors);

  return Promise
    .all([
      originCoverImagePromise,
      originLogoImagePromise,
      originGalleryImagesUrlsPromise,
      originUpdatesImagesUrlsPromise,
      originReviewsImagesUrlsPromise,
    ])
    .spread((coverImages, logoImages, galleryImages, updatesImages, reviewsImages) => {
      newDataSource[DATA_SOURCE_KEY_MAPPING.CoverImage] =
        _.first(coverImages) && ({
          Id: _.first(coverImages).IdString,
          Height: _.first(coverImages).Height,
          Width: _.first(coverImages).Width,
        });
      newDataSource[DATA_SOURCE_KEY_MAPPING.LogoImageId] =
        _.first(logoImages) && _.first(logoImages).IdString;
      newDataSource[DATA_SOURCE_KEY_MAPPING.Photos] =
        _.compact(_.map(
          galleryImages,
          ({ IdString }, index) => IdString && ({
            imageId: IdString,
            // keep consistent with logic in smart-component-gallery
            fbImageUrl: originalImages[index].Picture,
          })
        ));
      newDataSource[DATA_SOURCE_KEY_MAPPING.Posts] = transformPosts(originUpdates, updatesImages);
      newDataSource[DATA_SOURCE_KEY_MAPPING.Reviews] =
        transformReviews(
          originReviews,
          reviewsImages,
          dataSource[DATA_SOURCE_KEY_MAPPING.BusinessName]
        );
      const images = _.compact(_.union(
        coverImages,
        logoImages,
        galleryImages,
        updatesImages,
        reviewsImages
      ));
      return { fbDataSource: newDataSource, images };
    });
};

export const isFacebookImportSupported = siteConfig =>
  siteConfig.smartPageDataContext &&
  siteConfig.smartPageDataContext.UserId &&
  siteConfig.smartPageDataContext.PageId;

export const importFromFacebook =
  (siteConfig, smartPageService, dataFilter, hostSystemScenario) => {
    if (isFacebookImportSupported(siteConfig)) {
      return smartPageService
        .getFacebookPage(
          siteConfig.smartPageDataContext.PageId,
          { fbUserId: siteConfig.smartPageDataContext.UserId },
          dataFilter,
          hostSystemScenario
        );
    }
    return Promise.reject(new Error('NO_FB_AUTH'));
  };
